import { useEffect, useState } from "react";

/**
 * Terminology client for the web app.
 *
 * Talks to the consolidated authority at /api/terminology (v10 Sprint 1)
 * and always reports whether a result is authoritative.
 *
 * DEGRADED BY DESIGN
 *   The referential routes predate this API and currently render from local
 *   seed arrays. This client therefore falls back to those arrays rather than
 *   blanking the screen when the API is unreachable, so a terminology outage
 *   cannot hide a clinician's reference data.
 *
 *   The fallback result is explicitly marked `source: "LOCAL_SEED"`. A
 *   clinician can see that the code on screen is not backed by a verified
 *   release — that is the whole point of the provenance plumbing.
 */

export type TerminologySource = "AUTHORITATIVE_DB" | "LEGACY_SEED" | "LOCAL_SEED" | "UNAVAILABLE";

export interface TerminologyItem {
  code: string;
  display: string;
  source: TerminologySource;
  isAuthoritative: boolean;
  version: string | null;
}

const BASE = (import.meta.env.VITE_API_BASE_URL as string)
  || (import.meta.env.VITE_API_URL as string)
  || "/api";

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { Accept: "application/json" };
  try {
    const token = window.localStorage.getItem("dikdoc_token");
    if (token) h.Authorization = `Bearer ${token}`;
  } catch { /* storage unavailable (SSR, private mode) */ }
  return h;
}

async function getJson<T>(path: string, timeoutMs = 6000): Promise<T | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${BASE}${path}`, {
      headers: authHeaders(),
      signal: ctrl.signal,
      credentials: "include",
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null; // network error, abort, or API down
  } finally {
    clearTimeout(t);
  }
}

export interface TerminologyRelease {
  system: string;
  version: string;
  status: string | null;
  publisher: string | null;
  isAuthoritative: boolean;
  rowCount: number | null;
  notes: string | null;
}

/** Which release is actually loaded? Empty array means "nothing verified yet". */
export async function fetchReleases(): Promise<TerminologyRelease[]> {
  const data = await getJson<{ releases: TerminologyRelease[] }>("/terminology/releases");
  return data?.releases ?? [];
}

export async function searchTerminology(
  system: "LOINC" | "CIM10",
  query: string,
  limit = 30,
): Promise<TerminologyItem[] | null> {
  if (!query.trim()) return [];
  const data = await getJson<{
    results: Array<{
      code: string; display: string;
      release: { version: string | null } | null;
      isAuthoritative: boolean;
      source: TerminologySource;
    }>;
  }>(`/terminology/${system}?q=${encodeURIComponent(query)}&limit=${limit}`);

  if (!data) return null; // API unavailable — caller decides to use its local seed
  return data.results.map((r) => ({
    code: r.code,
    display: r.display,
    source: r.source,
    isAuthoritative: r.isAuthoritative === true,
    version: r.release?.version ?? null,
  }));
}

/**
 * Validate a code before it is committed to a clinical record.
 * Returns null when validation could not be performed, which callers MUST
 * treat as "unknown", not as "valid".
 */
export async function validateCode(
  system: "LOINC" | "CIM10",
  code: string,
): Promise<{ result: boolean; display?: string; message?: string; isAuthoritative: boolean } | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 6000);
  try {
    const res = await fetch(`${BASE}/terminology/validate`, {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ system, code }),
      signal: ctrl.signal,
      credentials: "include",
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(t);
  }
}

/** Warns a clinician when the codes on screen are not from a verified release. */
export function provenanceWarning(source: TerminologySource, version: string | null): string | null {
  switch (source) {
    case "AUTHORITATIVE_DB":
      return null;
    case "LOCAL_SEED":
      return "Référentiel local — non vérifié. Aucune version officielle n'est chargée.";
    case "UNAVAILABLE":
      return "Service de terminologie indisponible — la validité des codes n'a pas pu être vérifiée.";
    default:
      return version
        ? `Codes issus d'un jeu non certifié (${version}). Validation clinique requise.`
        : "Codes issus d'un jeu non certifié. Validation clinique requise.";
  }
}
/* ────────────────────────────────────────────────────────────────────────
 * Provenance banner state
 *
 * Three distinct states, all of which must be VISIBLE to the clinician:
 *   "loading"     request in flight
 *   "ok"          releases endpoint answered; banner describes the release
 *   "unavailable" the terminology API could not be reached at all
 *
 * The third state is the important one. A previous version returned [] on
 * failure and the routes rendered nothing when the list was empty, so a
 * terminology outage was indistinguishable from a clean page. That is a
 * safety problem: clinicians would read uncertified codes as if certified.
 * ──────────────────────────────────────────────────────────────────────── */

export type ProvenanceState =
  | { status: "loading" }
  | { status: "unavailable"; message: string }
  | { status: "ok"; release: TerminologyRelease | null; warning: string | null };

export function useTerminologyRelease(system: "LOINC" | "CIM10"): ProvenanceState {
  const [state, setState] = useState<ProvenanceState>({ status: "loading" });

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`${BASE}/terminology/releases?system=${system}`, {
          headers: authHeaders(), credentials: "include", signal: AbortSignal.timeout(6000),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { releases: TerminologyRelease[] };
        if (!alive) return;
        const release = data.releases?.find((r) => r.system === system) ?? null;
        if (!release) {
          // Answered, but nothing registered: still not an outage — say so.
          setState({
            status: "ok",
            release: null,
            warning: `Aucun ${system} certifié n'est chargé. Les codes affichés proviennent d'un jeu local non vérifié.`,
          });
          return;
        }
        setState({
          status: "ok",
          release,
          warning: release.isAuthoritative
            ? null
            : provenanceWarning("LEGACY_SEED", release.version),
        });
      } catch {
        if (!alive) return;
        setState({
          status: "unavailable",
          message: "Service de terminologie injoignable — les codes affichés n'ont pas pu être vérifiés.",
        });
      }
    })();
    return () => { alive = false; };
  }, [system]);

  return state;
}
